import { LoaderIcon } from "@/assets/icons";
import BigDataChart from "@/components/ui/chart/BigDataChart";
import DonutChart from "@/components/ui/chart/DonutChart";
import HorizontalBarChart from "@/components/ui/chart/HorizontalBarChart";
import VerticalBarChart from "@/components/ui/chart/VerticalBarChart";
import {
  AggregationType,
  WidgetAnalysisConfigResult,
  WidgetPreviewData,
  WidgetType
} from "@/types/widget";

import NoDataWidgetContent from "./NoDataWidgetContent";

interface WidgetConfigurationPreviewProps {
  widgetType: WidgetType;
  data: WidgetPreviewData;
  isLoading: boolean;
}

const WidgetConfigurationPreview = ({
  widgetType,
  data,
  isLoading
}: WidgetConfigurationPreviewProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <LoaderIcon className="animate-spin w-10 h-10 text-gray-500" />
        <p className="text-txtPlaceholder font-semibold">
          Mapping data to visualization...
        </p>
      </div>
    );
  }

  if (data?.length === 0) {
    return <NoDataWidgetContent />;
  }

  if (widgetType === WidgetType.BIG_NUMBER) {
    const item = data?.[0] as
      | { type: AggregationType; value: number; measure: string }
      | undefined;
    if (!item) return <NoDataWidgetContent />;
    return (
      <BigDataChart
        value={Number(item.value)}
        type={item.type}
        measure={item.measure}
      />
    );
  }

  const mappedData = (data as WidgetAnalysisConfigResult["data"])?.map(
    (item) => ({
      label: item.label,
      value: item.value
    })
  );

  switch (widgetType) {
    case WidgetType.BAR_V:
      return (
        <VerticalBarChart
          data={
            mappedData?.map((item) => ({
              name: item.label,
              value: Number(item.value),
              key: Number(item.value)
            })) || []
          }
        />
      );
    case WidgetType.BAR_H:
      return (
        <HorizontalBarChart
          data={
            mappedData?.map((item) => ({
              name: item.label,
              value: String(item.value)
            })) || []
          }
        />
      );
    case WidgetType.DONUT:
      return (
        <DonutChart
          data={
            mappedData?.map((item) => ({
              name: item.label,
              value: Number(item.value)
            })) || []
          }
        />
      );
    case WidgetType.LINE:
      return <NoDataWidgetContent />;
    default:
      return <NoDataWidgetContent />;
  }
};

export default WidgetConfigurationPreview;
